import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { select, Store } from '@ngrx/store';
import { Address, AddressList } from '../../model/address.model';
import { Profile } from '../../services/profile.model';
import * as fromApp from '../app.reducers';
import * as LabsysContactActions from './labsys-contact.actions';
import * as AuthActions from '../auth/auth.actions';
import {
    GetDeliveryAddressSuggestions,
    RequestError,
    SET_LOGGED_IN_CONTACT,
    SetIsContactChecked,
    SetLoggedInContact
} from './labsys-contact.actions';
import { environment } from '../../../environments/environment';
import { HeadersEnum, ParamsKeys, RestURLsEnum } from '../../model/http-request-enums';
import { Contact, ContactMapping, ContactType } from '../../model/contact.model';

import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Upsert } from '../../order-checkout/store/user-details/user-details.actions';
import { Upsert as UpsertDelivery } from '../../order-checkout/store/delivery-details/delivery-details.actions';
import { Upsert as UpsertInvoice } from '../../order-checkout/store/billing-details/billing-details.actions';
import { Upsert as UpsertOrderReference } from '../../order-checkout/store/order-references/order-references.actions';
import { UpsertOrderInformation } from '../../order-checkout/store/order-checkout/order-checkout.actions';

import { AuthConstants } from '../../constants/application-constants';
import { isValidAddress } from '../../order-checkout/shared/util';
import { ContactService, toContact } from '@app/services/contact.service';
import { ActivatedRoute, Router } from '@angular/router';
import { USER_INFO_LOADED } from '../auth/auth.actions';
import {
    UPDATE_PROFILE_SUCCESS,
    UpdateProfileSuccess
} from '@app/contact-profile/store/contact-profile.actions';

@Injectable()
export class LabsysContactEffects {

    contactUrl = environment.restBaseURL + RestURLsEnum.CONTACT_BASE;
    accountUrl = environment.restBaseURL + RestURLsEnum.ACCOUNT_BASE;
    header = new HttpHeaders().set(ParamsKeys.ACCEPT,
        [
            HeadersEnum.ACCEPT_CONTACT,
            HeadersEnum.ACCEPT_ORGANISATION, HeadersEnum.ACCEPT_ADDRESS_SUGGESTIONS,
            HeadersEnum.ACCEPT_ERROR,
        ].join(','));











    getLoggedInContact = createEffect(() => this.actions$.pipe(
        ofType(LabsysContactActions.GET_LOGGED_IN_CONTACT),
        switchMap((payload: LabsysContactActions.GetLoggedInContact) => this.contactService.getContact()
            .pipe(
                map(contact => ({ succeed: true, data: toContact(contact), authId: payload.authId })),
                catchError(() => [{ succeed: false, authId: payload.authId }])
            )),
        mergeMap((result: ContactResult) => {
            if (!!result.succeed) {
                return [new SetLoggedInContact(result.data)];
            }
            return [new SetIsContactChecked(true)];

        })
    ));

    // When the user has a contact in limbus not accepted yet the terms and conditions
    // redirect them to online and terms conditions page

    guardLegalUse = createEffect(() => this.actions$.pipe(
        ofType(LabsysContactActions.SET_LOGGED_IN_CONTACT),
        tap((payload: LabsysContactActions.SetLoggedInContact) => {
            const contact = payload.loggedInContact;
            if (!contact.portalTcAccepted) {
                const destination = this.route.snapshot['_routerState'].url;
                localStorage.setItem('auth-entry-url', destination);
                this.router.navigate(['/pages/online-terms-and-conditions']);
            }
        })
    ), { dispatch: false });


    updateAddressSuggestionsOnContactChange = createEffect(() => this.actions$.pipe(
        ofType(USER_INFO_LOADED),
        switchMap(() => [new GetDeliveryAddressSuggestions()])
    ));


    deliveryAddressSuggestions
        = createEffect(() => this.actions$.pipe(
            ofType(LabsysContactActions.GET_DELIVERY_ADDRESS_SUGGESTIONS),
            withLatestFrom(this.store.pipe(select(fromApp.getProfile)), this.store.pipe(select(fromApp.getLoggedInContact))),
            mergeMap(([{}, profile, contact]: [LabsysContactActions.GetDeliveryAddressSuggestions, Profile, Contact]) => {
                if (!!contact && contact.contactType !== ContactType.LEAD) {
                    return this.contactService.getDeliveryAddressSuggestions(contact.contactId).pipe(
                        catchError(err => [new RequestError(err)]));
                }
                // return empty addresses for leads
                return [];
            }),
            map((response: Address[] | RequestError) => {

                if (response instanceof RequestError) {
                    return response;
                }

                let updatedAddresses: Address[] = [];
                response.map((address: Address) => {
                    if (address.line1 !== '' && address.townOrCity !== '' && address.country !== '' && address.postCode !== '') {
                        updatedAddresses = [...updatedAddresses, address];
                    }
                });
                return {
                    type: LabsysContactActions.SET_DELIVERY_ADDRESS_SUGGESTIONS,
                    addresses: updatedAddresses
                };
            })
        ));



    updatedLocalStorageContactWhenTcAccepted = createEffect(() => this.actions$.pipe(
        ofType(LabsysContactActions.SET_TC_PORTAL_CONTACT),
        withLatestFrom(this.store.select(fromApp.getLoggedInContact)),
        map(([action, contact]) => {
            localStorage.setItem(AuthConstants.LOGGED_IN_CONTACT_LOCAL_STORAGE_KEY, JSON.stringify(contact));
        })
    ), { dispatch: false });

    /*
  * Initialize order-checkout state
  * */

    ContactToOrderCheckoutStateHandler
        = createEffect(() => this.actions$.pipe(
            ofType(...SET_CONTACT_TYPES),
            withLatestFrom(this.store.select('contact')),
            tap((args) => {
                userDetailsFn(this.store)(args);
                deliveryAndInvoiceFn(this.store)(args);

                this.store.dispatch(new SetIsContactChecked(true));

            })
        ), { dispatch: false });


    onUpdateProfileUpdateLocalStorage$ = createEffect(() => this.actions$.pipe(
        ofType(UPDATE_PROFILE_SUCCESS),
        map((action: UpdateProfileSuccess) => {
            localStorage.setItem(AuthConstants.LOGGED_IN_CONTACT_LOCAL_STORAGE_KEY, JSON.stringify(action.payload));
            return new SetLoggedInContact(action.payload);
        })
    ));

    constructor(private actions$: Actions,
        private store: Store<fromApp.AppState>,
        private contactService: ContactService,
        private router: Router,
        private route: ActivatedRoute
    ) {
    }
}

const SET_CONTACT_TYPES = [
    SET_LOGGED_IN_CONTACT,
    USER_INFO_LOADED
];

const userDetailsFn = (store) => ([action, contacts]) => {

    const contact = !!contacts.loggedInContact ? contacts.loggedInContact : null;

    if (!contact) {
        return;
    }

    const { firstName, lastName, email, phone, mobile } = contact;

    if (!!contact) {
        store.dispatch(new Upsert({ firstName, lastName, email, phone, mobile }));
        store.dispatch(new UpsertOrderInformation({
            firstName, lastName, email, phone, mobile
        }));
    }

    if (!!contacts.account) {
        const organisationId = contacts.account.id;
        const company = contacts.account.name;
        store.dispatch(new UpsertOrderInformation({ organisationId, company }));
    }

};


const deliveryAndInvoiceFn = (store) => ([action, contacts]) => {

    const contact = !!contacts.loggedInContact ? contacts.loggedInContact : null;

    if (!contact) {
        return;
    }

    if (!!contact) {
        let deliveryAddress = { ...contact.deliveryAddress };
        let billingDetails = { ...contact.postalAddress };

        const isDeliveryAddressValid  = isValidAddress(deliveryAddress);
        const isInvoiceAddressValid = isValidAddress(billingDetails);

        // business logic: use deliveryAddress when invoiceAddress is empty and vice versa
        if (!isDeliveryAddressValid && isInvoiceAddressValid) {
            deliveryAddress = { ...billingDetails };
        } else if (!isInvoiceAddressValid && isDeliveryAddressValid) {
            billingDetails = { ...deliveryAddress };
        }

        store.dispatch(new UpsertDelivery({ address: deliveryAddress }));
        store.dispatch(new UpsertInvoice(billingDetails));
        store.dispatch(new UpsertOrderInformation({
            deliveryAddress,
            invoiceAddress: billingDetails,
        }));

        if (!contacts.account) {
            const { name: company } = !!contact.organisation && contact.organisation.name ? contact.organisation : { name: '' };
            store.dispatch(new UpsertOrderReference({ company }));
            store.dispatch(new UpsertOrderInformation({ company }));
        }
    }
};

interface ContactResult {
    succeed: boolean;
    data:  Contact;
    authId: string;
}

